import React from 'react'
import {
    FormControlLabel,
    IconButton,
    Switch,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { Add } from '@material-ui/icons'
import { Container, EditSpan, FloorplanThumb } from '../../components'
import { useAppDispatch, useUser } from '../../hooks'
import { UnitConfig } from '../../models'

import { useInfrastructureConfigStyles } from './useInfrastructureConfigStyles'
import { updateUnitConfigReq } from '../../store'
import { isAccountManager } from '../../helpers'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    unitConfig: UnitConfig
    unitCount?: number
    onClickPlus: (UnitConfig: UnitConfig) => void
}

const toNumber = (value?: number | string | null): number | null => {
    if (value === null || value === undefined || value === '') {
        return null
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const formatRange = (
    min?: number | string | null,
    max?: number | string | null,
    format?: (value: number) => string,
) => {
    const formatValue =
        format ?? ((value: number) => value.toLocaleString('en-US'))
    const low = toNumber(min)
    const high = toNumber(max)
    if (low === null && high === null) {
        return null
    }
    if (low === null) {
        return formatValue(high as number)
    }
    if (high === null || low === high) {
        return formatValue(low)
    }
    return `${formatValue(low)}–${formatValue(high)}`
}

const formatRent = (value: number) => {
    const hasCents = value % 1 !== 0
    return `$${value.toLocaleString('en-US', {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
    })}`
}

export const UnitConfigRow = (props: Props) => {
    const { unitConfig, unitCount, onClickPlus } = props

    const styles = useInfrastructureConfigStyles()
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { workspaceUser } = useUser()

    const squareFootage = formatRange(
        unitConfig.min_square_footage,
        unitConfig.max_square_footage,
        (value) => `${value.toLocaleString('en-US')} sq ft`,
    )
    const marketRent = formatRange(
        unitConfig.min_market_rent,
        unitConfig.max_market_rent,
        (value) => `Market rent ${formatRent(value)}`,
    )
    const detailParts = [squareFootage, marketRent].filter(Boolean)
    const detailLine = detailParts.join(' · ')

    return (
        <Container
            style={{
                ...styles.rowStyle,
                ...styles.panelBorder,
                backgroundColor: theme.palette.grey[200],
                alignItems: 'center',
            }}
        >
            <FloorplanThumb
                src={unitConfig.floorplan_image}
                alt={`${unitConfig.name} floor plan`}
                thumbStyle={{
                    width: 88,
                    height: 56,
                    objectFit: 'contain',
                    backgroundColor: theme.palette.common.white,
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    marginRight: theme.spacing(1.5),
                    flexShrink: 0,
                }}
            />
            <Container
                style={{
                    flexDirection: 'column',
                    marginRight: theme.spacing(1),
                }}
            >
                <EditSpan
                    style={{
                        ...typographyStyle(theme.typography.body1),
                        fontWeight: 700,
                    }}
                    editIconTooltip="Edit Name"
                    onSave={(name) => {
                        dispatch(
                            updateUnitConfigReq({
                                id: unitConfig.id,
                                body: {
                                    name: name,
                                },
                            }),
                        )
                    }}
                >
                    {unitConfig.name}
                </EditSpan>
                {unitCount !== undefined && (
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            fontWeight: 400,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {unitCount} {unitCount === 1 ? 'unit' : 'units'}
                    </span>
                )}
                {detailLine !== '' && (
                    <span
                        style={{
                            ...typographyStyle(theme.typography.body2),
                            fontWeight: 400,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {detailLine}
                    </span>
                )}
                <FormControlLabel
                    labelPlacement="end"
                    label="Shows on Schedule"
                    style={{ marginRight: theme.spacing(2) }}
                    control={
                        <Switch
                            checked={unitConfig.shows_on_schedule}
                            color="primary"
                            onClick={() => {
                                dispatch(
                                    updateUnitConfigReq({
                                        id: unitConfig.id,
                                        body: {
                                            shows_on_schedule: !unitConfig.shows_on_schedule,
                                        },
                                    }),
                                )
                            }}
                        />
                    }
                />
            </Container>

            <div style={{ flex: 1 }} />
            {isAccountManager(workspaceUser) && (
                <Tooltip title="Add Areas">
                    <IconButton onClick={() => onClickPlus(unitConfig)}>
                        <Add />
                    </IconButton>
                </Tooltip>
            )}
        </Container>
    )
}
